import React, { useState, useMemo } from 'react';
import { makeStyles } from '@material-ui/core/styles';
import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TableSortLabel,
  LinearProgress,
  Typography,
  IconButton,
  Button,
  Menu,
  MenuItem,
  Box,
} from '@material-ui/core';
import { Alert } from '@material-ui/lab';
import * as Icons from '@material-ui/icons';

/**
 * Tabla densa reutilizable. Reemplaza a @material-ui/data-grid.
 *
 * COLUMNAS (`columns`): array de objetos
 *   { id, label, align?, width?, sortable?, type?, ellipsis?, render?, format?, sortValue? }
 *
 * MODOS de paginación (`mode`):
 *   - 'client' (default): pagina y ordena en memoria TODAS las filas.
 *   - 'cursor': paginación server-side controlada por el contenedor.
 */

const useStyles = makeStyles((theme) => ({
  wrapper: {
    position: 'relative',
    width: '100%',
  },
  progressBar: {
    position: 'absolute',
    top: 0,
    left: 0,
    right: 0,
    zIndex: 3,
    height: 2,
  },
  container: {
    width: '100%',
  },
  table: {
    width: '100%',
  },
  headCell: {
    backgroundColor: '#f4f6f5',
    color: '#37474f',
    fontWeight: 600,
    fontSize: '0.72rem',
    textTransform: 'uppercase',
    letterSpacing: 0.4,
    lineHeight: 1.2,
    padding: '8px 12px',
    borderBottom: `2px solid ${theme.palette.divider}`,
    whiteSpace: 'nowrap',
  },
  bodyCell: {
    fontSize: '0.8125rem',
    padding: '4px 12px',
    borderBottom: `1px solid ${theme.palette.divider}`,
    whiteSpace: 'nowrap',
  },
  ellipsisCell: {
    maxWidth: 1,
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
  },
  numberCell: {
    fontVariantNumeric: 'tabular-nums',
    fontWeight: 600,
  },
  row: {
    height: 36,
    '&:nth-of-type(even)': { backgroundColor: '#fafafa' },
    '&:hover': { backgroundColor: 'rgba(0, 179, 31, 0.08)' },
  },
  emptyState: {
    textAlign: 'center',
    color: theme.palette.text.secondary,
    padding: theme.spacing(6, 2),
  },
  footer: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    padding: theme.spacing(0.5, 1.5),
    borderTop: `1px solid ${theme.palette.divider}`,
    flexWrap: 'wrap',
    gap: theme.spacing(1),
  },
}));

function compareRows(a, b, col, dir) {
  const get = (r) => (col.sortValue ? col.sortValue(r) : r[col.id]);
  let va = get(a);
  let vb = get(b);
  const isNum = col.type === 'number' || (typeof va === 'number' && typeof vb === 'number');
  if (isNum) {
    return ((Number(va) || 0) - (Number(vb) || 0)) * dir;
  }
  if (col.type === 'date') {
    return (new Date(va).getTime() - new Date(vb).getTime()) * dir;
  }
  va = (va == null ? '' : String(va)).toLowerCase();
  vb = (vb == null ? '' : String(vb)).toLowerCase();
  if (va < vb) return -1 * dir;
  if (va > vb) return 1 * dir;
  return 0;
}

function DataTable(props) {
  const {
    columns,
    rows = [],
    getRowId = (row) => row.id,
    loading = false,
    error = null,
    emptyMessage = 'No hay datos para mostrar.',
    emptyIcon: EmptyIcon = Icons.InboxOutlined,
    mode = 'client',
    maxHeight = 'calc(100vh - 300px)',
    minWidth,
    rowClassName,
    initialSort,
    // cursor mode
    page = 0,
    hasMore = false,
    total = 0,
    pageSize,
    pageSizeOptions = [10, 25, 50, 100],
    onPageChange,
    onPageSizeChange,
    // client mode
    initialPageSize = 10,
  } = props;

  const classes = useStyles();

  const [orderBy, setOrderBy] = useState(initialSort ? initialSort.orderBy : null);
  const [order, setOrder] = useState(initialSort ? initialSort.order : 'asc');
  const [clientPage, setClientPage] = useState(0);
  const [clientPageSize, setClientPageSize] = useState(initialPageSize);
  const [pageSizeAnchor, setPageSizeAnchor] = useState(null);

  const handleSort = (col) => {
    if (col.sortable === false) return;
    if (orderBy === col.id) {
      setOrder((o) => (o === 'asc' ? 'desc' : 'asc'));
    } else {
      setOrderBy(col.id);
      setOrder('asc');
    }
  };

  const sortedRows = useMemo(() => {
    if (!orderBy) return rows;
    const col = columns.find((c) => c.id === orderBy);
    if (!col) return rows;
    const dir = order === 'asc' ? 1 : -1;
    return [...rows].sort((a, b) => compareRows(a, b, col, dir));
  }, [rows, columns, orderBy, order]);

  const effectivePageSize = mode === 'cursor' ? pageSize : clientPageSize;
  const visibleRows = useMemo(() => {
    if (mode === 'cursor') return sortedRows;
    const start = clientPage * clientPageSize;
    return sortedRows.slice(start, start + clientPageSize);
  }, [sortedRows, mode, clientPage, clientPageSize]);

  const clientTotalPages = Math.max(1, Math.ceil(sortedRows.length / clientPageSize));
  const currentPage = mode === 'cursor' ? page : clientPage;

  const goPrev = () => {
    if (mode === 'cursor') onPageChange && onPageChange('prev');
    else setClientPage((p) => Math.max(0, p - 1));
  };
  const goNext = () => {
    if (mode === 'cursor') onPageChange && onPageChange('next');
    else setClientPage((p) => Math.min(clientTotalPages - 1, p + 1));
  };
  const canPrev = currentPage > 0 && !loading;
  const canNext = (mode === 'cursor' ? hasMore : clientPage < clientTotalPages - 1) && !loading;

  const changePageSize = (s) => {
    setPageSizeAnchor(null);
    if (mode === 'cursor') {
      onPageSizeChange && onPageSizeChange(s);
    } else {
      setClientPageSize(s);
      setClientPage(0);
    }
  };

  const cellClass = (col) => {
    let cls = classes.bodyCell;
    if (col.ellipsis) cls += ` ${classes.ellipsisCell}`;
    if (col.type === 'number' || col.align === 'right') cls += ` ${classes.numberCell}`;
    return cls;
  };

  const renderCell = (col, row) => {
    if (col.render) return col.render(row);
    const value = row[col.id];
    if (col.format) return col.format(value, row);
    return value == null || value === '' ? '—' : value;
  };

  const colSpan = columns.length;

  const rangeLabel = () => {
    if (mode === 'cursor') {
      return total > 0 ? `${total} registros` : '';
    }
    if (sortedRows.length === 0) return '0 registros';
    const start = clientPage * clientPageSize + 1;
    const end = Math.min(sortedRows.length, (clientPage + 1) * clientPageSize);
    return `${start}–${end} de ${sortedRows.length}`;
  };

  return (
    <div className={classes.wrapper}>
      {loading && <LinearProgress className={classes.progressBar} />}
      {error && <Alert severity="error" style={{ margin: 12 }}>{error}</Alert>}

      <TableContainer className={classes.container} style={{ maxHeight }}>
        <Table className={classes.table} size="small" stickyHeader style={minWidth ? { minWidth } : undefined}>
          <TableHead>
            <TableRow>
              {columns.map((col) => (
                <TableCell
                  key={col.id}
                  align={col.align || 'left'}
                  className={classes.headCell}
                  style={col.width ? { width: col.width, minWidth: col.width } : undefined}
                  sortDirection={orderBy === col.id ? order : false}
                >
                  {col.sortable === false ? (
                    col.label
                  ) : (
                    <TableSortLabel
                      active={orderBy === col.id}
                      direction={orderBy === col.id ? order : 'asc'}
                      onClick={() => handleSort(col)}
                    >
                      {col.label}
                    </TableSortLabel>
                  )}
                </TableCell>
              ))}
            </TableRow>
          </TableHead>
          <TableBody>
            {!loading && visibleRows.length === 0 && (
              <TableRow>
                <TableCell colSpan={colSpan}>
                  <div className={classes.emptyState}>
                    <EmptyIcon style={{ fontSize: 40, opacity: 0.4 }} />
                    <Typography variant="body2">{emptyMessage}</Typography>
                  </div>
                </TableCell>
              </TableRow>
            )}
            {visibleRows.map((row) => {
              const extra = rowClassName ? rowClassName(row) : '';
              return (
                <TableRow key={getRowId(row)} className={`${classes.row} ${extra}`} hover>
                  {columns.map((col) => (
                    <TableCell key={col.id} align={col.align || 'left'} className={cellClass(col)}
                      style={col.width ? { maxWidth: col.width } : undefined}>
                      {renderCell(col, row)}
                    </TableCell>
                  ))}
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
      </TableContainer>

      <div className={classes.footer}>
        <Box display="flex" alignItems="center" style={{ gap: 8 }}>
          <Typography variant="body2" color="textSecondary">Filas:</Typography>
          <Button size="small" onClick={(e) => setPageSizeAnchor(e.currentTarget)} endIcon={<Icons.ArrowDropDown />}>
            {effectivePageSize}
          </Button>
          <Menu anchorEl={pageSizeAnchor} open={Boolean(pageSizeAnchor)} onClose={() => setPageSizeAnchor(null)}>
            {pageSizeOptions.map((s) => (
              <MenuItem key={s} selected={s === effectivePageSize} onClick={() => changePageSize(s)}>{s}</MenuItem>
            ))}
          </Menu>
          <Typography variant="body2" color="textSecondary">· {rangeLabel()}</Typography>
        </Box>
        <Box display="flex" alignItems="center" style={{ gap: 4 }}>
          <IconButton size="small" onClick={goPrev} disabled={!canPrev}><Icons.ChevronLeft /></IconButton>
          <Typography variant="body2">
            Página {currentPage + 1}{mode === 'cursor' ? '' : ` de ${clientTotalPages}`}
          </Typography>
          <IconButton size="small" onClick={goNext} disabled={!canNext}><Icons.ChevronRight /></IconButton>
        </Box>
      </div>
    </div>
  );
}

export default DataTable;
